export const AdvanceSettingsConfig = (t, pressure) => ({
  outlierRemoval: {
    n_neighbors: {
      key: "clusterPoints",
      minValue: 5,
      maxValue: 1000000,
      label: t("advanceSettings.clusterPoints"),
      ranges: { hard: { min: 5, max: 1000000 } },
      integer: true,
    },
    window: {
      key: "medianCurvePoints",
      minValue: 5,
      maxValue: 1000000,
      label: t("advanceSettings.medianCurvePoints"),
      ranges: { hard: { min: 5, max: 1000000 } },
      integer: true,
    },
    n: {
      key: "maxRelativeDistance",
      minValue: 1,
      maxValue: 5,
      label: t("advanceSettings.maxRelativeDistance"),
      ranges: { hard: { min: 1, max: 5 } },
    },
  },
  cipAndShutdownIdentification: {
    // Pressure Drop Threshold Options (Checkbox Group)
    pressure_drop_threshold: {
      label: t("advanceSettings.pressureDropThreshold"),
      type: "checkboxGroup",
      defaultSelected: "percentage",
      options: {
        percentage: {
          key: "permeateFlowDropThreshold",
          minValue: 10,
          maxValue: 100,
          label: t("advanceSettings.pressureDropPercentThreshold"),
          defaultValue: 10,
          ranges: { hard: { min: 5, max: 100 } },
        },
        absolute: {
          key: "pressureDropThresholdAbsolute",
          minValue: pressure === "psig" ? 10 : 0.7,
          maxValue: pressure === "psig" ? 100 : 6.9,
          label: t("advanceSettings.pressureDropAbsThreshold").replace(
            "<unit>",
            pressure
          ),
          defaultValue: pressure === "psig" ? 15 : 1.0,
          ranges: {
            hard: {
              min: pressure === "psig" ? 10 : 0.7,
              max: pressure === "psig" ? 100 : 6.9,
            },
          },
        },
      },
    },

    // Salt Passage Change (Radio Group)
    use_salt_passage: {
      key: "useSaltPassageChange",
      type: "radio",
      label: t("advanceSettings.useSaltPassageChange"),
      options: [
        {
          name: t("ValueStrings.yes"),
          value: "true",
          key: "use_salt_passage_yes",
        },
        {
          name: t("ValueStrings.no"),
          value: "false",
          key: "use_salt_passage_no",
        },
      ],
    },

    // Minimum Days Between CIPs
    min_day_between_cips: {
      key: "minDaysBetweenCIPs",
      type: "number",
      minValue: 1,
      maxValue: 90,
      label: t("advanceSettings.minDaysBetweenCIPs"),
      ranges: { hard: { min: 1, max: 90 } },
      integer: true,
    },

    // Additional Settings (if needed)
    max_days_between_cips: {
      key: "maxDaysBetweenCIPs",
      type: "number",
      minValue: 1,
      maxValue: 365,
      label: t("advanceSettings.maxDaysBetweenCIPs"),
      ranges: { hard: { min: 1, max: 365 } },
      integer: true,
    },
    k: {
      key: "cipIdentificationResolution",
      type: "number",
      minValue: 1,
      maxValue: 10,
      label: t("advanceSettings.cipIdentificationResolution"),
      ranges: { hard: { min: 1, max: 10 } },
    },
    shutdown_length: {
      key: "minIdleDaysShutdown",
      type: "number",
      minValue: 5,
      maxValue: 50,
      label: t("advanceSettings.minIdleDaysShutdown"),
      ranges: { hard: { min: 5, max: 50 } },
      integer: true,
    },
  },
  cleaningEffectivenessCalculation: {
    max_time: {
      key: "maxStabilizationDaysAfterCIP",
      minValue: 1,
      maxValue: 30,
      label: t("advanceSettings.maxStabilizationDaysAfterCIP"),
      ranges: { hard: { min: 1, max: 30 } },
      integer: true,
    },
    min_time: {
      key: "minStabilizationDaysAfterCIP",
      minValue: 0.01,
      maxValue: 1,
      label: t("advanceSettings.minStabilizationDaysAfterCIP"),
      ranges: { hard: { min: 0.01, max: 1 } },
    },
    min_num_points: {
      key: "minPointsForCleaningEffectiveness",
      minValue: 5,
      maxValue: 100,
      label: t("advanceSettings.minPointsForCleaningEffectiveness"),
      ranges: { hard: { min: 5, max: 100 } },
      integer: true,
    },
    stabilization_days: {
      key: "deafultStabilizationDaysAfterCIP",
      label: t("advanceSettings.deafultStabilizationDaysAfterCIP"),
      integer: true,
    },
  },
  cipPredictionAndRecommendation: {
    top_exp: {
      key: "foulingAggressivenessFactor",
      minValue: 1,
      maxValue: 20,
      label: t("advanceSettings.foulingAggressivenessFactor"),
      ranges: { hard: { min: 1, max: 20 } },
    },
    max_evals: {
      key: "numberOfIterationsToFindOptima",
      minValue: 5,
      maxValue: 100,
      label: t("advanceSettings.numberOfIterationsToFindOptima"),
      ranges: { hard: { min: 5, max: 100 } },
      integer: true,
    },
    adapt_pf_trend: {
      key: "adaptPermeateFlowDropThreshold",
      isBoolean: true,
      label: t("advanceSettings.adaptPermeateFlowDropThreshold"),
      ranges: { hard: { min: 10, max: 20 } },
      options: {
        option1: {
          name: t("ValueStrings.yes"),
          value: "true",
          key: "adapt_pf_trend_yes",
        },
        option2: {
          name: t("ValueStrings.no"),
          value: "false",
          key: "adapt_pf_trend_no",
        },
      },
    },
    adapt_pressure_drop_trend: {
      key: "adaptPressureDropThreshold",
      isBoolean: true,
      label: t("advanceSettings.adaptPressureDropThreshold"),
      options: {
        option1: {
          name: t("ValueStrings.yes"),
          value: "true",
          key: "adapt_pressure_drop_trend_yes",
        },
        option2: {
          name: t("ValueStrings.no"),
          value: "false",
          key: "adapt_pressure_drop_trend_no",
        },
      },
    },
    adapt_salt_passage_trend: {
      key: "adaptSaltPassageThreshold",
      isBoolean: true,
      label: t("advanceSettings.adaptSaltPassageThreshold"),
      options: {
        option1: {
          name: t("ValueStrings.yes"),
          value: "true",
          key: "adapt_salt_passage_trend_yes",
        },
        option2: {
          name: t("ValueStrings.no"),
          value: "false",
          key: "adapt_salt_passage_trend_no",
        },
      },
    },
    simulation_improve_accuracy: {
      key: "simulationImproveAccuracy",
      isBoolean: true,
      label: t("advanceSettings.simulationImproveAccuracy"),
      options: {
        option1: {
          name: t("ValueStrings.yes"),
          value: "true",
          key: "simulation_improve_accuracy_yes",
        },
        option2: {
          name: t("ValueStrings.no"),
          value: "false",
          key: "simulation_improve_accuracy_no",
        },
      },
    },
  },
  lifetime: {
    failure_probability_threshold: {
      key: "failure_probability_threshold",
      minValue: 50,
      maxValue: 100,
      label: t("advanceSettings.failureProbabilityThreshold"),
      ranges: { hard: { min: 50, max: 100 } },
    },
  },
});
